import React from 'react';
import Button from '@app/react-common/components/button/Button';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';
import { NameDescriptionFormData } from './NameDescriptionEditView';
import { CollapsibleSection } from '../components';
import { BasePreviewViewProps, SelectedMethodInfo } from './types';

interface NameDescriptionPreviewViewProps extends BasePreviewViewProps {
  formData: NameDescriptionFormData;
}

export const NameDescriptionPreviewView = ({
  onBack,
  onClose,
  onSaveChanges,
  formData,
  selectedMethods,
}: NameDescriptionPreviewViewProps) => {
  const getNewNoteValue = (currentNote: string): string => {
    switch (formData.noteAction) {
      case 'change':
        // Empty note input means keep current note
        return formData.note.trim() ? formData.note : currentNote;
      case 'remove':
        return '';
      default:
        return currentNote;
    }
  };

  const getNewNameValue = (currentName: string): string => {
    if (formData.name.trim()) {
      return formData.name;
    }
    return currentName;
  };

  const formatDisplayValue = (value: string): React.ReactNode => {
    if (!value) {
      return <span className="bulk-edit-modal__preview-empty">{'{empty}'}</span>;
    }
    return value;
  };

  const methodHasChanges = (method: SelectedMethodInfo): boolean => {
    const hasNameChange = method.name !== getNewNameValue(method.name);
    const hasNoteChange = method.note !== getNewNoteValue(method.note);
    return hasNameChange || hasNoteChange;
  };

  const changedMethods = selectedMethods.filter(methodHasChanges);
  const unchangedMethods = selectedMethods.filter((m) => !methodHasChanges(m));
  const affectedCount = changedMethods.length;
  const isSaveDisabled = affectedCount === 0;

  return (
    <div className="bulk-edit-modal__view">
      <div className="bulk-edit-modal__header bulk-edit-modal__header--with-subtitle">
        <div className="bulk-edit-modal__header__left">
          <div className="bulk-edit-modal__header__titles">
            <h2>Confirm changes</h2>
            <span className="bulk-edit-modal__header__subtitle">
              {affectedCount} of {selectedMethods.length} Methods Will Be Affected
            </span>
          </div>
        </div>
        <Button icon="close" variant="ghost" size="small" onClick={onClose} />
      </div>
      <div className="bulk-edit-modal__content">
        {changedMethods.length > 0 && (
          <CollapsibleSection title="Changed" count={changedMethods.length} defaultOpen={true}>
            {changedMethods.map((method) => {
              const currentName = method.name;
              const newName = getNewNameValue(currentName);
              const hasNameChange = currentName !== newName;

              const currentNote = method.note;
              const newNote = getNewNoteValue(currentNote);
              const hasNoteChange = currentNote !== newNote;

              return (
                <div key={method.id} className="bulk-edit-modal__preview-item">
                  <div className="bulk-edit-modal__preview-item__header">{currentName || '(No name)'}</div>
                  <div className="bulk-edit-modal__preview-item__group">{method.groupName}</div>
                  <div className="bulk-edit-modal__preview-item__change">
                    <span className="bulk-edit-modal__preview-item__change__label">Name:</span>
                    {hasNameChange ? (
                      <>
                        <span className="bulk-edit-modal__preview-item__change__old">
                          {formatDisplayValue(currentName)}
                        </span>
                        <span className="bulk-edit-modal__preview-item__change__arrow">&gt;</span>
                        <span className="bulk-edit-modal__preview-item__change__new">
                          {formatDisplayValue(newName)}
                        </span>
                      </>
                    ) : (
                      <span className="bulk-edit-modal__preview-item__change__unchanged">
                        {formatDisplayValue(currentName)} (unchanged)
                      </span>
                    )}
                  </div>
                  <div className="bulk-edit-modal__preview-item__change">
                    <span className="bulk-edit-modal__preview-item__change__label">Note:</span>
                    {hasNoteChange ? (
                      <>
                        <span className="bulk-edit-modal__preview-item__change__old">
                          {formatDisplayValue(currentNote)}
                        </span>
                        <span className="bulk-edit-modal__preview-item__change__arrow">&gt;</span>
                        <span className="bulk-edit-modal__preview-item__change__new">
                          {formatDisplayValue(newNote)}
                        </span>
                      </>
                    ) : (
                      <span className="bulk-edit-modal__preview-item__change__unchanged">
                        {formatDisplayValue(currentNote)} (unchanged)
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </CollapsibleSection>
        )}
        {unchangedMethods.length > 0 && (
          <CollapsibleSection title="Unchanged" count={unchangedMethods.length} defaultOpen={false}>
            {unchangedMethods.map((method) => (
              <div key={method.id} className="bulk-edit-modal__preview-item">
                <div className="bulk-edit-modal__preview-item__header">{method.name || '(No name)'}</div>
                <div className="bulk-edit-modal__preview-item__group">{method.groupName}</div>
                <div className="bulk-edit-modal__preview-item__change">
                  <span className="bulk-edit-modal__preview-item__change__label">Name:</span>
                  <span className="bulk-edit-modal__preview-item__change__unchanged">
                    {formatDisplayValue(method.name)} (unchanged)
                  </span>
                </div>
                <div className="bulk-edit-modal__preview-item__change">
                  <span className="bulk-edit-modal__preview-item__change__label">Note:</span>
                  <span className="bulk-edit-modal__preview-item__change__unchanged">
                    {formatDisplayValue(method.note)} (unchanged)
                  </span>
                </div>
              </div>
            ))}
          </CollapsibleSection>
        )}
      </div>
      <div className="bulk-edit-modal__footer">
        <Button text="Back" onClick={onBack} size="medium" variant="secondary" />
        {isSaveDisabled ? (
          <Tooltip tooltipText="No methods will be affected by this change" maxWidth={220}>
            <Button
              text="Save Changes"
              onClick={() => onSaveChanges(changedMethods.map((m) => m.id))}
              size="medium"
              variant="primary"
              disabled
            />
          </Tooltip>
        ) : (
          <div className="bulk-edit-modal__button-wrapper">
            <Button
              text="Save Changes"
              onClick={() => onSaveChanges(changedMethods.map((m) => m.id))}
              size="medium"
              variant="primary"
            />
          </div>
        )}
      </div>
    </div>
  );
};
